import { useState, useMemo, useEffect } from 'react';
import { Plus, Trash2, CheckCircle, ChevronDown, ChevronRight, Calendar, BookOpen, Clock, LayoutGrid } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import Modal from '../../components/common/Modal';
import { formatDate, generateId } from '../../utils/helpers';
import DateInput from '../../components/common/DateInput';

const subjectPill = [
  'bg-blue-100 text-blue-700', 'bg-green-100 text-green-700', 'bg-purple-100 text-purple-700',
  'bg-orange-100 text-orange-700', 'bg-pink-100 text-pink-700', 'bg-teal-100 text-teal-700',
  'bg-yellow-100 text-yellow-800', 'bg-red-100 text-red-700',
];

const emptyForm = { title: '', description: '', classId: '', subjectId: '', dueDate: '', status: 'active' };

export default function HomeworkPage() {
  const { homework, setHomework, classes, subjects, students, parents, setNotifications, hwCompletions, setHwCompletions, teacherMapping, loadTeacherMapping, saveHomework, deleteHomework } = useApp();
  const { currentUser } = useAuth();

  useEffect(() => {
    if (currentUser?.role === 'teacher') loadTeacherMapping();
  }, [currentUser, loadTeacherMapping]);
  const today = new Date().toISOString().split('T')[0];

  const isAdmin    = currentUser?.role === 'admin';
  const isTeacher  = currentUser?.role === 'teacher';
  const isParent   = ['parent','father','mother'].includes(currentUser?.role);
  const isStudent  = currentUser?.role === 'student';
  const canEdit    = isAdmin || isTeacher;

  // Teacher role breakdown
  const isClassTeacher    = isTeacher && teacherMapping.isClassTeacher;
  const myTeachClassIds   = new Set(isClassTeacher ? [teacherMapping.classId] : teacherMapping.assignedClassIds);
  const myTeachSubjectIds = new Set(teacherMapping.assignedSubjectIds);
  const myPairs           = teacherMapping.classSubjectPairs || [];
  const profId            = currentUser?.profileId;

  // Parent: resolve children
  const myChildren   = isParent ? students.filter(s => (s.parentId === profId || s.id === profId)) : [];
  const [selectedChildId, setSelectedChildId] = useState(myChildren[0]?.id || null);
  const selectedChild = myChildren.find(c => c.id === selectedChildId) || myChildren[0];

  // Student: resolve own class
  const studentRecord = isStudent ? students.find(s => s.id === profId) : null;

  // Which classIds are visible to this user?
  const visibleClassIds = isAdmin
    ? classes.map(c => c.id)
    : isTeacher
      ? [...myTeachClassIds]
      : isStudent
        ? [studentRecord?.classId].filter(Boolean)
        : [selectedChild?.classId].filter(Boolean);

  // Filter homework to visible classes + teacher-specific rules
  const visibleHw = homework.filter(h => {
    if (isAdmin) return true;
    if (isTeacher) {
      if (!myTeachClassIds.has(h.classId)) return false;
      // Class teacher sees all homework in their class
      if (isClassTeacher) return true;
      // Subject teacher sees only their own homework
      return h.assignedBy === profId || myTeachSubjectIds.has(h.subjectId);
    }
    return visibleClassIds.includes(h.classId);
  });

  // Date filter
  const [dateFilter, setDateFilter] = useState('all');
  const filteredHw = dateFilter === 'today'    ? visibleHw.filter(h => h.dueDate === today)
                   : dateFilter === 'upcoming' ? visibleHw.filter(h => h.dueDate > today)
                   : dateFilter === 'past'     ? visibleHw.filter(h => h.dueDate < today)
                   : visibleHw;

  // Collapsed state
  const [collapsedDates, setCollapsedDates] = useState({});
  const [collapsedClasses, setCollapsedClasses] = useState({});
  const toggleDate  = (d)  => setCollapsedDates(p  => ({ ...p, [d]:  !p[d]  }));
  const toggleClass = (key) => setCollapsedClasses(p => ({ ...p, [key]: !p[key] }));

  // Teacher: mark completion per student
  const [markModal, setMarkModal]   = useState(null); // holds the hw object
  const [markSel,   setMarkSel]     = useState({});   // studentId → bool
  const openMarkModal = (hw) => {
    const existing = new Set((hwCompletions || []).filter(c => c.homeworkId === hw.id).map(c => c.studentId));
    const init = {};
    students.filter(s => s.classId === hw.classId && s.status === 'active').forEach(s => { init[s.id] = existing.has(s.id); });
    setMarkSel(init);
    setMarkModal(hw);
  };
  const saveMarkModal = () => {
    const hw = markModal;
    setHwCompletions(prev => {
      const kept = (prev || []).filter(c => c.homeworkId !== hw.id);
      const added = Object.entries(markSel).filter(([, v]) => v).map(([sid]) => ({
        id: generateId('HWC'), homeworkId: hw.id, studentId: sid,
        teacherId: currentUser?.id, date: today, confirmedAt: new Date().toISOString(),
      }));
      return [...kept, ...added];
    });
    setMarkModal(null);
  };

  // Classes/subjects available for assignment
  const assignableClasses = isAdmin
    ? classes
    : isClassTeacher
      ? classes.filter(c => c.id === teacherMapping.classId)
      : classes.filter(c => myTeachClassIds.has(c.id));

  const getAssignableSubjects = (classId) => {
    const forClass = subjects.filter(s => (s.classIds || []).includes(classId));
    const list = forClass.length > 0 ? forClass : subjects;
    if (isAdmin || isClassTeacher) return list;
    // Subject teacher: only subjects they are assigned to in this specific class
    const pairedSubjectIds = new Set(
      myPairs.filter(p => p.classId === classId).map(p => p.subjectId)
    );
    return list.filter(s => pairedSubjectIds.has(s.id));
  };

  const canAssignAllSubjects = isAdmin || isClassTeacher;
  const canDeleteHw = (hw) => isAdmin || (isTeacher && hw.assignedBy === profId);

  // Single-assign modal
  const [modal, setModal] = useState(false);
  const [form, setForm]   = useState({ ...emptyForm });
  const openAdd = (classId = '', subjectId = '', dueDate = '') => {
    const defaultClass = classId || assignableClasses[0]?.id || '';
    setForm({ ...emptyForm, classId: defaultClass, subjectId, dueDate });
    setModal(true);
  };

  // Bulk-assign modal (all subjects at once) — class teachers only
  const [bulkModal, setBulkModal] = useState(false);
  const [bulkClassId, setBulkClassId] = useState(assignableClasses[0]?.id || '');
  const [bulkDueDate, setBulkDueDate] = useState('');
  const bulkSubjects = useMemo(
    () => getAssignableSubjects(bulkClassId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [subjects, bulkClassId, teacherMapping]
  );
  const [bulkRows, setBulkRows] = useState({});
  const openBulk = () => {
    setBulkClassId(assignableClasses[0]?.id || '');
    setBulkDueDate('');
    setBulkRows({});
    setBulkModal(true);
  };
  const setBulkField = (subjectId, field, value) =>
    setBulkRows(prev => ({ ...prev, [subjectId]: { ...prev[subjectId], [field]: value } }));

  const saveBulk = async () => {
    const cls = classes.find(c => c.id === bulkClassId);
    const newItems = [];
    const newNotifs = [];
    bulkSubjects.forEach(sub => {
      const row = bulkRows[sub.id];
      if (!row?.title?.trim()) return; // skip empty rows
      const hwId = generateId('HW');
      newItems.push({ id: hwId, title: row.title.trim(), description: row.description || '',
        classId: bulkClassId, subjectId: sub.id, dueDate: bulkDueDate,
        assignedDate: today, status: 'active', assignedBy: currentUser?.profileId || 'STF-001' });
      newNotifs.push({ id: generateId('NTF'), type: 'homework',
        title: `New Homework: ${row.title.trim()}`,
        body: `${cls ? `${cls.name}-${cls.section}` : ''} | ${sub.name} | Due: ${bulkDueDate}`,
        classId: bulkClassId, homeworkId: hwId,
        createdAt: new Date().toISOString(), targetRoles: ['parent'], readBy: [] });
    });
    if (newItems.length === 0) return;
    for (const item of newItems) { await saveHomework(item); }
    setNotifications(prev => [...newNotifs, ...prev]);
    setBulkModal(false);
  };

  const save = async () => {
    const hwId  = generateId('HW');
    const newHw = { ...form, id: hwId, assignedDate: today, assignedBy: currentUser?.profileId || 'STF-001' };
    await saveHomework(newHw);
    const cls = classes.find(c => c.id === form.classId);
    const sub = subjects.find(s => s.id === form.subjectId);
    setNotifications(prev => [{
      id: generateId('NTF'), type: 'homework',
      title: `New Homework: ${form.title}`,
      body: `${cls ? `${cls.name}-${cls.section}` : ''} | ${sub?.name || ''} | Due: ${form.dueDate}`,
      classId: form.classId, homeworkId: hwId,
      createdAt: new Date().toISOString(), targetRoles: ['parent'], readBy: [],
    }, ...prev]);
    setModal(false);
    setForm({ ...emptyForm });
  };

  const remove = (id) => { if (window.confirm('Delete?')) deleteHomework(id); };

  const isConfirmed = (hw) => {
    if (!isParent && !isStudent) return false;
    const childId = isStudent ? studentRecord?.id : selectedChild?.id;
    if (!childId) return false;
    // Accept teacher-marked OR parent-marked completions — just check studentId
    return (hwCompletions || []).some(
      c => c.homeworkId === hw.id && c.studentId === childId
    );
  };
  const markDone = (hw) => setHwCompletions(prev => [...(prev || []), {
    id: generateId('HWC'), homeworkId: hw.id, studentId: selectedChild?.id,
    parentId: currentUser?.id, date: today, confirmedAt: new Date().toISOString(),
  }]);
  const completionCount = (hwId) => new Set((hwCompletions || []).filter(c => c.homeworkId === hwId).map(c => c.studentId)).size;
  const classStudentCount = (cid) => students.filter(s => s.classId === cid && s.status === 'active').length;

  // Build tree: Date ? ClassId ? SubjectId ? [hw]
  const tree = {};
  filteredHw.forEach(hw => {
    const d = hw.dueDate || 'Unknown';
    const c = hw.classId;
    const s = hw.subjectId;
    if (!tree[d]) tree[d] = {};
    if (!tree[d][c]) tree[d][c] = {};
    if (!tree[d][c][s]) tree[d][c][s] = [];
    tree[d][c][s].push(hw);
  });
  const sortedDates = Object.keys(tree).sort((a, b) => b.localeCompare(a));

  const getClass   = (id) => classes.find(c => c.id === id);
  const getSubject = (id) => subjects.find(s => s.id === id);

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold">Homework</h1>
          <p className="text-gray-500 text-sm">{filteredHw.length} assignment{filteredHw.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Child selector */}
          {isParent && myChildren.length > 1 && myChildren.map(c => (
            <button key={c.id} onClick={() => setSelectedChildId(c.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${c.id === selectedChild?.id ? 'bg-blue-900 text-white border-blue-900' : 'bg-white border-gray-300 hover:bg-gray-50'}`}>
              {c.firstName}
            </button>
          ))}
          {/* Date filters */}
          {['all','today','upcoming','past'].map(f => (
            <button key={f} onClick={() => setDateFilter(f)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border capitalize transition-colors ${dateFilter === f ? 'bg-blue-900 text-white border-blue-900' : 'bg-white border-gray-300 hover:bg-gray-50'}`}>
              {f}
            </button>
          ))}
          {(isAdmin || isTeacher) && (
            <>
              {canAssignAllSubjects && (
                <button onClick={openBulk}
                  className="flex items-center gap-2 text-white px-4 py-1 rounded-lg text-sm font-medium bg-green-600 hover:bg-green-700">
                  <LayoutGrid size={15} /> Assign All Subjects
                </button>
              )}
              <button onClick={() => openAdd()}
                className="flex items-center gap-2 text-white px-4 py-1 rounded-lg text-sm font-medium" style={{ backgroundColor: '#6b21a8' }}>
                <Plus size={15} /> Assign
              </button>
            </>
          )}
        </div>
      </div>

      {sortedDates.length === 0 && (
        <div className="bg-white rounded-xl p-12 text-center">No homework found</div>
      )}

      {/* Date groups */}
      <div className="space-y-4">
        {sortedDates.map(date => {
          const isToday  = date === today;
          const isPast   = date < today;
          const dateOpen = !collapsedDates[date];
          const classIds = Object.keys(tree[date]);
          const totalInDate = classIds.reduce((n, cid) => n + Object.values(tree[date][cid]).flat().length, 0);

          return (
            <div key={date} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Date header */}
              <button onClick={() => toggleDate(date)}
                className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-3">
                  <Calendar size={16} className={isToday ? 'text-blue-700' : isPast ? 'text-gray-400' : 'text-orange-500'} />
                  <span className={`font-bold text-sm ${isToday ? 'text-blue-800' : isPast ? 'text-gray-500' : 'text-orange-600'}`}>
                    {isToday ? 'Today — ' : ''}{formatDate(date)}
                  </span>
                  {isToday && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">Due Today</span>}
                  <span className="">{totalInDate} task{totalInDate !== 1 ? 's' : ''} · {classIds.length} class{classIds.length !== 1 ? 'es' : ''}</span>
                </div>
                {dateOpen ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
              </button>

              {/* Class groups */}
              {dateOpen && (
                <div className="border-t border-gray-100 divide-y divide-gray-50">
                  {classIds.map(classId => {
                    const cls      = getClass(classId);
                    const classKey = `${date}__${classId}`;
                    const classOpen = !collapsedClasses[classKey];
                    const subjectIds = Object.keys(tree[date][classId]);
                    const totalInClass = subjectIds.reduce((n, sid) => n + tree[date][classId][sid].length, 0);

                    return (
                      <div key={classId}>
                        {/* Class sub-header */}
                        <button onClick={() => toggleClass(classKey)}
                          className="w-full flex items-center justify-between px-6 py-2.5 bg-gray-50 hover:bg-gray-100 transition-colors">
                          <div className="flex items-center gap-2">
                            <BookOpen size={14} className="text-blue-800" />
                            <span className="font-semibold text-sm">
                              {cls ? `Class ${cls.name} – Section ${cls.section}` : classId}
                            </span>
                            <span className="">{totalInClass} task{totalInClass !== 1 ? 's' : ''} · {subjectIds.length} subject{subjectIds.length !== 1 ? 's' : ''}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {(isAdmin || (isTeacher && myTeachClassIds.has(classId))) && (
                              <span onClick={e => { e.stopPropagation(); openAdd(classId, '', date); }}
                                className="text-blue-700 bg-blue-50 hover:bg-blue-100 px-2 py-0.5 rounded-lg font-medium flex items-center gap-1 transition-colors">
                                <Plus size={11} /> Add
                              </span>
                            )}
                            {classOpen ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                          </div>
                        </button>

                        {/* Subject rows */}
                        {classOpen && (
                          <div className="px-6 py-3 space-y-3">
                            {subjectIds.map((subjectId, idx) => {
                              const sub  = getSubject(subjectId);
                              const pill = subjectPill[idx % subjectPill.length];
                              const hwItems = tree[date][classId][subjectId];

                              return (
                                <div key={subjectId} className="flex gap-3">
                                  {/* Subject label */}
                                  <div className="flex-shrink-0 w-32 pt-0.5">
                                    <span className={`inline-flex items-center gap-1 font-bold px-2.5 py-1 rounded-full ${pill}`}>
                                      {sub?.name || subjectId}
                                    </span>
                                    {(isAdmin || (isTeacher && myTeachClassIds.has(classId) && (isClassTeacher || myTeachSubjectIds.has(subjectId)))) && (
                                      <button onClick={() => openAdd(classId, subjectId, date)}
                                        className="mt-1 ml-1 hover:text-blue-600 transition-colors">
                                        <Plus size={13} />
                                      </button>
                                    )}
                                  </div>

                                  {/* Homework items */}
                                  <div className="flex-1 space-y-1.5">
                                    {hwItems.map(hw => {
                                      const confirmed  = isConfirmed(hw);
                                      const cCount     = completionCount(hw.id);
                                      const total      = classStudentCount(hw.classId);
                                      const allDone    = total > 0 && cCount >= total;
                                      const someDone   = cCount > 0 && !allDone;
                                      const pct        = total > 0 ? Math.round((cCount / total) * 100) : 0;

                                      // Derived status badge
                                      const statusBadge = (() => {
                                        if (isParent || isStudent) {
                                          return confirmed
                                            ? <span className="flex items-center gap-1 font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full"><CheckCircle size={11}/>Completed</span>
                                            : <span className="font-semibold bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full">Pending</span>;
                                        }
                                        if (allDone)   return <span className="flex items-center gap-1 font-semibold bg-green-100 text-green-700 px-2 py-0.5 rounded-full"><CheckCircle size={11}/>All Done</span>;
                                        if (someDone)  return <span className="font-semibold bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">In Progress</span>;
                                        return <span className="font-semibold bg-gray-100 px-2 py-0.5 rounded-full">Not Started</span>;
                                      })();

                                      return (
                                        <div key={hw.id} className={`flex items-start justify-between gap-3 rounded-lg px-3 py-1 border transition-colors ${
                                          confirmed || allDone ? 'bg-green-50 border-green-200' : someDone ? 'bg-blue-50/40 border-blue-100' : 'bg-gray-50 border-gray-100'
                                        }`}>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                              <span className={`font-semibold text-sm ${confirmed || allDone ? 'text-green-800' : 'text-gray-800'}`}>{hw.title}</span>
                                              {statusBadge}
                                            </div>
                                            {hw.description && <p className="mt-0.5">{hw.description}</p>}
                                            <div className="flex items-center gap-3 mt-1 flex-wrap">
                                              <span className="flex items-center gap-1"><Clock size={10} /> Assigned {formatDate(hw.assignedDate)}</span>
                                              {canEdit && total > 0 && (
                                                <span className="flex items-center gap-2">
                                                  <span className={`flex items-center gap-1 font-medium ${allDone ? 'text-green-600' : someDone ? 'text-blue-600' : 'text-gray-400'}`}>
                                                    <CheckCircle size={10} />{cCount}/{total} confirmed
                                                  </span>
                                                  <div className="w-16 bg-gray-200 rounded-full h-1.5">
                                                    <div className="h-1.5 rounded-full bg-green-500 transition-all" style={{ width: `${pct}%` }} />
                                                  </div>
                                                  <span className="text-gray-400">{pct}%</span>
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                          <div className="flex items-center gap-1.5 flex-shrink-0">
                                            {(isAdmin || isTeacher) && (
                                              <button onClick={() => openMarkModal(hw)}
                                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg font-semibold bg-blue-900 hover:bg-blue-800 text-white text-xs">
                                                <CheckCircle size={12} /> Mark Students
                                              </button>
                                            )}
                                            {canDeleteHw(hw) && (
                                              <button onClick={() => remove(hw.id)} className="p-1.5 hover:bg-red-50 rounded text-red-400">
                                                <Trash2 size={13} />
                                              </button>
                                            )}
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bulk Assign Modal — all subjects at once */}
      <Modal isOpen={bulkModal} onClose={() => setBulkModal(false)} title="Assign Homework — All Subjects">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Class</label>
              <select value={bulkClassId} onChange={e => { setBulkClassId(e.target.value); setBulkRows({}); }}
                className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
                {assignableClasses.map(c => <option key={c.id} value={c.id}>{c.name} – {c.section}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Due Date</label>
              <DateInput value={bulkDueDate} onChange={e => setBulkDueDate(e.target.value)}
                className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-3 py-1 font-semibold w-28">Subject</th>
                  <th className="text-left px-3 py-1 font-semibold">Homework Title</th>
                  <th className="text-left px-3 py-1 font-semibold">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {bulkSubjects.map((sub, idx) => (
                  <tr key={sub.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-3 py-1">
                      <span className={`font-bold px-2 py-0.5 rounded-full ${subjectPill[idx % subjectPill.length]}`}>
                        {sub.name}
                      </span>
                    </td>
                    <td className="px-3 py-1">
                      <input type="text" placeholder="Title (leave blank to skip)"
                        value={bulkRows[sub.id]?.title || ''}
                        onChange={e => setBulkField(sub.id, 'title', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </td>
                    <td className="px-3 py-1">
                      <input type="text" placeholder="Optional description"
                        value={bulkRows[sub.id]?.description || ''}
                        onChange={e => setBulkField(sub.id, 'description', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="">Subjects with empty titles will be skipped.</p>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={() => setBulkModal(false)} className="px-4 py-1 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={saveBulk}
            disabled={!bulkDueDate || !Object.values(bulkRows).some(r => r?.title?.trim())}
            className="px-4 py-1 text-sm text-white rounded-lg bg-green-600 hover:bg-green-700 disabled:opacity-50">
            Save All
          </button>
        </div>
      </Modal>

      {/* Assign Modal */}
      <Modal isOpen={modal} onClose={() => setModal(false)} title="Assign Homework">
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Title</label>
            <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" placeholder="Homework title" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Description</label>
            <textarea rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none" placeholder="Instructions..." />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Due Date</label>
            <DateInput value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))}
              className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Class</label>
            <select value={form.classId} onChange={e => setForm(f => ({ ...f, classId: e.target.value, subjectId: '' }))}
              className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">Select Class</option>
              {assignableClasses.map(c => <option key={c.id} value={c.id}>{c.name} – {c.section}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Subject</label>
            <select value={form.subjectId} onChange={e => setForm(f => ({ ...f, subjectId: e.target.value }))}
              className="w-full px-3 py-1 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400">
              <option value="">Select Subject</option>
              {getAssignableSubjects(form.classId).map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-4 pt-4 border-t">
          <button onClick={() => setModal(false)} className="px-4 py-1 text-sm hover:bg-gray-100 rounded-lg">Cancel</button>
          <button onClick={save} disabled={!form.title || !form.classId || !form.subjectId || !form.dueDate}
            className="px-4 py-1 text-sm text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#6b21a8' }}>
            Assign
          </button>
        </div>
      </Modal>

      {/* Teacher: mark per-student completion */}
      <Modal isOpen={!!markModal} onClose={() => setMarkModal(null)} title={`Mark Completion — ${markModal?.title || ''}`}>
        <p className="text-sm text-gray-500 mb-3">Check each student who submitted this homework.</p>
        <div className="max-h-80 overflow-y-auto space-y-1 mb-4">
          {students.filter(s => s.classId === markModal?.classId && s.status === 'active')
            .sort((a, b) => Number(a.rollNumber) - Number(b.rollNumber))
            .map(s => (
              <label key={s.id} className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 cursor-pointer">
                <input type="checkbox" checked={!!markSel[s.id]}
                  onChange={e => setMarkSel(p => ({ ...p, [s.id]: e.target.checked }))}
                  className="w-4 h-4 rounded accent-blue-900" />
                <span className="text-sm font-medium">{s.firstName} {s.lastName}</span>
                <span className="text-xs text-gray-400 ml-auto">Roll {s.rollNumber}</span>
              </label>
            ))}
        </div>
        <div className="flex justify-between items-center pt-3 border-t">
          <span className="text-xs text-gray-500">
            {Object.values(markSel).filter(Boolean).length} / {Object.keys(markSel).length} marked
          </span>
          <div className="flex gap-2">
            <button onClick={() => setMarkModal(null)} className="px-4 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={saveMarkModal} className="px-4 py-1.5 text-sm font-semibold text-white bg-blue-900 hover:bg-blue-800 rounded-lg">Save</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
